import { useEffect, useMemo, useState } from 'react'
import exceptionReporter from '@aytm/lib/common/exception-reporter'

import { ChatAssistant, ChatError, SuggestedPromptsSection } from '../../types'
import { GLOBAL_PROMPTS_TO_SHOW, pickRandomPrompts } from '../globalSuggestedPrompts'

import styles from './SkipperSuggestedPrompts.module.scss'

export type SkipperSuggestedPromptsProps = {
  assistant: ChatAssistant
}

export default function SkipperSuggestedPrompts({ assistant }: SkipperSuggestedPromptsProps) {
  const [, refresh] = useState<object>({})
  useEffect(() => assistant.listen(() => void refresh({})), [assistant])

  const suggested = assistant.suggestedPrompts
  const globalPool = suggested?.global?.prompts
  // Stable pick for the lifetime of this mount — re-randomizes only when the
  // pool reference changes or the component remounts (e.g. chat reset).
  const randomizedGlobalPrompts = useMemo(
    () => (globalPool ? pickRandomPrompts(globalPool, GLOBAL_PROMPTS_TO_SHOW) : []),
    [globalPool]
  )

  if (!suggested) {
    return null
  }

  const { pageSpecific, global } = suggested
  const hasPageSpecific = pageSpecific && pageSpecific.prompts.length > 0
  const hasGlobal = global && randomizedGlobalPrompts.length > 0

  if (!hasPageSpecific && !hasGlobal) {
    return null
  }

  const disabled = !assistant.inputReady

  const sendPrompt = (text: string) => {
    if (disabled) {
      return
    }
    assistant.send(text, { suggestedPrompt: true }).then(
      result => {
        if (result instanceof ChatError) {
          exceptionReporter(result, {
            context: 'Skipper Chat: SkipperSuggestedPrompts, send prompt',
            text,
            error: result
          })
        }
      },
      error => {
        exceptionReporter(error, {
          context: 'Skipper Chat: SkipperSuggestedPrompts, send prompt',
          text
        })
      }
    )
  }

  const renderSection = (section: SuggestedPromptsSection, variant: 'pageSpecific' | 'global', key: string) => {
    const promptsToShow = 'global' === variant ? randomizedGlobalPrompts : section.prompts
    return (
      <div className={styles.SkipperSuggestedPrompts__section} key={key}>
        <div className={styles.SkipperSuggestedPrompts__sectionLabel}>{section.label}</div>
        <div className={styles.SkipperSuggestedPrompts__pills}>
          {promptsToShow.map((prompt, index) => {
            const pill = (
              <button
                key={`${key}-${index}`}
                type="button"
                disabled={disabled || Boolean(prompt.disabled)}
                className={`${styles.SkipperSuggestedPrompts__pill} ${
                  'pageSpecific' === variant
                    ? styles['SkipperSuggestedPrompts__pill--pageSpecific']
                    : styles['SkipperSuggestedPrompts__pill--global']
                }`}
                onClick={prompt.disabled ? undefined : () => sendPrompt(prompt.text ?? prompt.label)}
              >
                {prompt.label}
              </button>
            )
            // The hint rides the wrapper, never the pill: a disabled button reports no pointer
            // events, so a tooltip hung off it is mute in the one state that has something to say.
            return prompt.hint ? (
              <span key={`${key}-${index}`} className={styles.SkipperSuggestedPrompts__hint} title={prompt.hint}>
                {pill}
              </span>
            ) : (
              pill
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles.SkipperSuggestedPrompts} ${disabled ? styles['SkipperSuggestedPrompts--disabled'] : ''}`}>
      {hasPageSpecific && renderSection(pageSpecific, 'pageSpecific', 'page')}
      {hasGlobal && renderSection(global, 'global', 'global')}
    </div>
  )
}
